import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  CircularProgress
} from '@mui/material';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase-config';
import { getAuth } from 'firebase/auth';

// List of carriers and their forwarding codes
const CARRIERS = [
  {
    value: 'att',
    label: 'AT&T',
    activationPrefix: '*61*',
    activationSuffix: '#',
    deactivateCode: '#61#',
    instructions: 'Activate Missed Call Forwarding: Dial {activateCode} (Forwards calls when unanswered.)'
  },
  {
    value: 'verizon',
    label: 'Verizon',
    activationPrefix: '*71',
    activationSuffix: '',
    deactivateCode: '*73',
    instructions: 'Activate Conditional Call Forwarding (Missed Calls): Dial {activateCode} (Forwards calls when unanswered or busy.)'
  },
  {
    value: 'tmobile',
    label: 'T-Mobile',
    activationPrefix: '**61*',
    activationSuffix: '**10#',
    deactivateCode: '##61#',
    instructions: 'Activate Missed Call Forwarding: Dial {activateCode} (Forwards calls when unanswered.)'
  },
  {
    value: 'sprint',
    label: 'Sprint (Now T-Mobile)',
    activationPrefix: '**61*',
    activationSuffix: '**10#',
    deactivateCode: '##61#',
    instructions: 'Activate Missed Call Forwarding: Dial {activateCode} (Forwards calls when unanswered.)'
  },
  {
    value: 'uscellular',
    label: 'US Cellular',
    activationPrefix: '*92',
    activationSuffix: '',
    deactivateCode: '*93',
    instructions: 'Activate Missed Call Forwarding: Dial {activateCode} (Forwards calls when unanswered.)'
  },
  {
    value: 'other',
    label: 'Other Carriers',
    activationPrefix: '**61*',
    activationSuffix: '#',
    deactivateCode: '##61#',
    instructions: 'Activate Missed Call Forwarding: Dial {activateCode} (For most other carriers)'
  }
];

// Formats a phone number to E.164 format
const formatPhoneNumber = (number) => {
  if (!number) return '';

  // Strip all non-digit characters
  const digitsOnly = number.replace(/\D/g, '');

  // For US numbers, ensure the country code is added
  if (digitsOnly.length === 10) {
    return `+1${digitsOnly}`;
  }

  // If it already has a country code (11+ digits), just add the +
  return `+${digitsOnly}`;
};

// Validates a phone number in E.164 format
const isValidPhoneNumber = (number) => {
  return /^\+\d{10,15}$/.test(number);
};

const PhoneSetupStep = ({ data, onUpdate, onNext }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [phoneData, setPhoneData] = useState(data.phoneData || {
    phoneNumber: '',
    carrier: ''
  });

  // Get the current user ID
  const auth = getAuth();
  const userId = auth.currentUser?.uid;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPhoneData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!userId) {
        throw new Error("User ID is missing. Please try logging in again.");
      }

      // Validate form data
      if (!phoneData.phoneNumber) {
        throw new Error("Please enter your business phone number.");
      }

      if (!phoneData.carrier) {
        throw new Error("Please select your mobile carrier.");
      }

      // Format and validate the phone number
      const formattedPhoneNumber = formatPhoneNumber(phoneData.phoneNumber);

      if (!isValidPhoneNumber(formattedPhoneNumber)) {
        throw new Error("Please enter a valid phone number with the +1 country code (format: +18433383154).");
      }

      // Find carrier details
      const selectedCarrier = CARRIERS.find(c => c.value === phoneData.carrier);
      const carrierDetails = selectedCarrier ? {
        name: selectedCarrier.label,
        activationPrefix: selectedCarrier.activationPrefix,
        activationSuffix: selectedCarrier.activationSuffix,
        deactivateCode: selectedCarrier.deactivateCode,
        instructionsTemplate: selectedCarrier.instructions
      } : null;

      // Create the document reference and data
      const businessRef = doc(db, 'businesses', userId);

      // Update phone data with formatted number
      const updatedPhoneData = {
        ...phoneData,
        phoneNumber: formattedPhoneNumber,
        carrierDetails
      };

      // Save to Firestore
      await setDoc(businessRef, {
        phoneData: updatedPhoneData,
        updatedAt: new Date().toISOString()
      }, { merge: true });

      // Get the business name from the data
      const businessName = data.businessInfo?.name || 'Your Business';

      // Get the Twilio number if it exists
      const twilioNumber = data.twilioNumber || data.phoneData?.twilioNumber;

      // If we have a Twilio number, send call forwarding instructions
      if (twilioNumber) {
        try {
          console.log('Sending call forwarding instructions...');

          // Call the Cloud Function to send call forwarding instructions
          const response = await fetch('https://us-central1-auto-text-back-web-app.cloudfunctions.net/sendCallForwardingInstructions', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              businessPhone: formattedPhoneNumber,
              twilioNumber: twilioNumber,
              carrier: phoneData.carrier,
              businessName: businessName,
              userId: userId
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            console.error('Error sending call forwarding instructions:', errorData);
            // Continue even if sending instructions fails
          } else {
            console.log('Call forwarding instructions sent successfully');
          }
        } catch (instructionsError) {
          console.error('Error sending call forwarding instructions:', instructionsError);
          // Continue even if sending instructions fails
        }
      } else {
        console.log('No Twilio number found, skipping call forwarding instructions');
      }

      setSuccess(true);

      // Update parent component
      onUpdate({
        ...data,
        phoneData: updatedPhoneData
      });

      // Wait a moment to show success message before proceeding
      setTimeout(() => {
        onNext();
      }, 1000);
    } catch (err) {
      console.error('Error saving phone setup:', err);
      setError(err.message || 'Failed to save phone setup');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h6" gutterBottom>
        Phone Setup
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>Phone settings saved successfully!</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Enter your business phone number with the +1 country code and select your carrier.
              We'll provide you with instructions to set up call forwarding for missed calls.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Business Phone Number"
              name="phoneNumber"
              value={phoneData.phoneNumber}
              onChange={handleChange}
              placeholder="+18433383154"
              helperText="Enter your phone number with the +1 country code (format: +18433383154)"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel id="carrier-label">Mobile Carrier</InputLabel>
              <Select
                labelId="carrier-label"
                name="carrier"
                value={phoneData.carrier || ''}
                label="Mobile Carrier"
                onChange={handleChange}
              >
                {CARRIERS.map(carrier => (
                  <MenuItem key={carrier.value} value={carrier.value}>
                    {carrier.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Select your mobile carrier to get specific call forwarding instructions
              </FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Next'}
        </Button>
      </Box>
    </Box>
  );
};

export default PhoneSetupStep;